import { Card, Checkbox } from '@radix-ui/themes'
import { CardHeader, CardTitle, CardContent } from './ui/card'
import { CheckCircle, CircleX, Loader2, LockKeyhole, Mail} from 'lucide-react'
import { Button } from './ui/button'
import { Label } from "@/components/ui/label";
import { Input } from './ui/input'
import { useInvitations } from '@/hooks/useInvitations'
import logo from "/asset/logo-removebg-preview.png";
import { useEffect, useState } from 'react'
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from '@/hooks/use-toast';
import { navigate } from 'wouter/use-browser-location';
import TermsAndConditionsContent from './TermsAndConditionsContent';

const RegisterInvitedMember = () => {
  const invitationToken = new URLSearchParams(window.location.search).get('token');
  const { 
    preprocessingInvitation,
    isLoading,
    isError,
    error,
    registerInvitedMember
  } = useInvitations()
  const [termsModalOpen, setTermsModalOpen] = useState(false);

  const formSchema = z.object({
    email: z.string().min(1, "Email is required").email("Invalid email address"),
    password: z.string()
      .min(8, "Password must be at least 8 characters")
      .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
      .regex(/[a-z]/, "Password must contain at least one lowercase letter")
      .regex(/[0-9]/, "Password must contain at least one number")
      .regex(/[^A-Za-z0-9]/, "Password must contain at least one special character"),
    confirmPassword: z.string(),
    termsAccepted: z.boolean().refine(val => val, {
      message: "You must agree to the Terms & Conditions before creating an account."
    }),
  }).refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

  type FormData = z.infer<typeof formSchema>;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
      termsAccepted: false,
    }
  });

  useEffect(() => {
    if (!preprocessingInvitation) {
      return
    }

    setValue("email", preprocessingInvitation.email)
  }, [preprocessingInvitation])

  const onSubmit = async (data: FormData) => {
    await registerInvitedMember({ email: data.email, password: data.password }, {
      onSuccess: () => {
        toast({
          title: "Account register successfully",
          description: `${data.email} had been registered. Redirecting you to the login page ......`,
          duration: 5000,
          icon: <CheckCircle size={30} className="animate-spin-once text-green-400"/>,
        });
        setTimeout(() => {navigate("/login")}, 5000)
      },
      onError: (error) => {
        console.error(JSON.stringify(error))
        toast({
          title: "Error",
          description: `Failed to create account: ${JSON.stringify(error)}`,
          duration: 5000,
          variant: "destructive",
          icon: <CircleX size={40} className="text-red-400"/>,
        });
      }
    })
  };

  if (!invitationToken) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-blue-600 to-purple-700">
        <Card className="w-full max-w-xl mx-4 bg-white/90 backdrop-blur-sm shadow-xl">
          <CardHeader className="space-y-1">
            <CircleX size={70} className="w-full text-red-500" />
            {/* <CardTitle className="text-3xl text-gray-700 text-center font-medium">Invitation token not found</CardTitle> */}
          </CardHeader>
          <CardContent>
            <p className='text-center font-medium text-xl'>Invitation token not provided</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-blue-600 to-purple-700">
        <Card className="w-full max-w-xl mx-4 bg-white/90 backdrop-blur-sm shadow-xl">
          <CardHeader className="space-y-1">

          </CardHeader>
          <CardContent>
            <div className='flex-col grid gap-6 justify-center'>
              <Loader2 size={70} className='animate-spin text-gray-500 w-full' />
              <p className='animate-pulse text-gray-600 text-center'>Prepraring your registration ......</p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if(isError || !preprocessingInvitation) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-blue-600 to-purple-700">
        <Card className="w-full max-w-xl mx-4 bg-white/90 backdrop-blur-sm shadow-xl">
          <CardHeader className="space-y-1">
            <CircleX size={70} className="w-full text-red-500" />
            {/* <CardTitle className="text-3xl text-gray-700 text-center font-medium">Invitation token not found</CardTitle> */}
          </CardHeader>
          <CardContent>
            <p className='text-center font-medium text-xl'>{error?.message || "An error occur"}</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-blue-600 to-purple-700">
      <Card className="w-full max-w-md mx-4 bg-white/90 backdrop-blur-sm shadow-xl relative z-10">
        <div className="flex flex-col">
          <img className="mt-3 mx-auto w-48" src={logo} />
        </div>
        <CardHeader className="space-y-1 pt-0">
          <CardTitle className="text-lg text-center font-medium">
            Cloud E Invoice
          </CardTitle>
          <p className="text-center text-gray-600">
            Please enter your password to Sign Up
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="mt-2 mb-8">
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <Input
                  type="email"
                  placeholder="Email"
                  className="pl-10"
                  disabled
                  {...register("email")}
                />
                {
                  errors.email 
                    && 
                  <p className='text-red-500 text-sm'>
                    {errors.email.message}
                  </p>
                }
              </div>
            </div>
            <div className="space-y-2">
              <div className="relative">
                <Label required>Password</Label>
                <LockKeyhole className="absolute left-3 top-9 h-5 w-5 text-gray-400" />
                <Input
                  type="password"
                  placeholder="Password"
                  className="pl-10"
                  {...register("password")}
                />
                {
                  errors.password 
                    && 
                  <p className='text-red-500 text-sm'>
                    {errors.password.message}
                  </p>
                }
              </div>
            </div>
            <div className="space-y-2">
              <div className="relative">
                <Label required>Confirm Password</Label>
                <LockKeyhole className="absolute left-3 top-9 h-5 w-5 text-gray-400" />
                <Input
                  type="password"
                  placeholder="Password"
                  className="pl-10"
                  {...register("confirmPassword")}
                />
                {
                  errors.confirmPassword 
                    && 
                  <p className='text-red-500 text-sm'>
                    {errors.confirmPassword.message}
                  </p>
                }
              </div>
            </div>

            <div className="mt-4">
              <div className="flex items-center space-x-2">
                <Button
                  type="button"
                  variant="link"
                  className="text-blue-600 p-0"
                  onClick={() => {
                    setTermsModalOpen(true);
                  }}
                  >
                  View Terms and Conditions
                </Button>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="terms"
                  checked={watch("termsAccepted")}
                  onCheckedChange={(checked) => {
                    setValue("termsAccepted", !!checked, { shouldValidate: true });
                  }}
                  {...register("termsAccepted")}
                />
                <Label
                  htmlFor="terms"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  I accept the Terms and Conditions
                </Label>
              </div>
              {errors.termsAccepted && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.termsAccepted.message}
                </p>
              )}
            </div>

            <Button
              type='submit'
              className='bg-blue-500 my-6 w-full hover:bg-blue-700'
              variant="default"
              disabled={!watch("termsAccepted")}
              >
              Sign Up
            </Button>
          </form>
        </CardContent>
      </Card>

      <Dialog open={termsModalOpen} onOpenChange={setTermsModalOpen}>
        <DialogContent className="max-h-[85vh] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Terms and Conditions</DialogTitle>
            <DialogDescription className="sr-only">
              The terms governing your use of the QuickBill platform.
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto pr-1">
            <TermsAndConditionsContent />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setTermsModalOpen(false)}
            >
              Close
            </Button>
            <Button
              onClick={() => {
                setValue("termsAccepted", true, { shouldValidate: true })
                setTermsModalOpen(false);
              }}
            >
              Accept Terms
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}


export default RegisterInvitedMember